import { BASE_RULES_KEY, type RuleResponse, type RuleArray } from '@core/types'
import { inject } from '#imports'

export const useRules = () => {
    // Global composer instead of useI18n(): this composable is also called from the
    // `phone-input` Nuxt plugin, where useI18n() throws (no component setup context).
    const { t } = useNuxtApp().$i18n

    if (import.meta.client) {
        const baseRules = inject(BASE_RULES_KEY, undefined)

        if (!baseRules) {
            throw new Error('Rules not provided!')
        }

        const {
            required,
            minLength,
            noNumbers,
            noSpecialCharacters,
            EMAIL,
            EXISTING_PASSWORD,
            NEW_PASSWORD
        } = baseRules

        function phone(value?: string): RuleResponse {
            if (!value) return true

            const strippedNo = value.replace('+45', '').replace(/\s/g, '')

            return strippedNo?.length === 8 || t('core.validationRules.phone.invalid')
        }

        function ssn(value?: string): RuleResponse {
            if (!value) return true

            if (value.length < 11) return t('core.validationRules.ssn.invalid')

            return true
        }

        const PHONE: RuleArray = [
            phone,
        ]

        function hasLastName(value?: string): RuleResponse {
            if (!value) return true

            return value.trim().split(/\s+/).length >= 2 || t('core.validationRules.fullName.missingLastName')
        }

        const FULLNAME: RuleArray = [
            required(t('core.validationRules.required.fullName')),
            minLength(2, t('core.validationRules.minLength.name')),
            noNumbers,
            noSpecialCharacters,
            hasLastName,
        ]

        const FIRSTNAME: RuleArray = [
            required(t('core.validationRules.required.firstName')),
            minLength(2, t('core.validationRules.minLength.name')),
            noNumbers,
            noSpecialCharacters,
        ]

        const LASTNAME: RuleArray = [
            required(t('core.validationRules.required.lastName')),
            minLength(2, t('core.validationRules.minLength.name')),
            noNumbers,
            noSpecialCharacters,
        ]

        const SSN: RuleArray = [
            required(t('core.validationRules.required.ssn')),
            ssn,
        ]

        const USERNAME: RuleArray = [
            required(),
        ]

        return {
            required,
            PHONE,
            FULLNAME,
            FIRSTNAME,
            LASTNAME,
            EMAIL,
            EXISTING_PASSWORD,
            NEW_PASSWORD,
            SSN,
            USERNAME,
        }
    }
    else {
        return {
            // SSR no-op stub; mirrors the client `required(message?)` signature so consumers stay typed.
            required: (_message?: string) => (): RuleResponse => true,
            PHONE: [],
            FULLNAME: [],
            FIRSTNAME: [],
            LASTNAME: [],
            EMAIL: [],
            EXISTING_PASSWORD: [],
            NEW_PASSWORD: [],
            SSN: [],
            USERNAME: [],
        }
    }
}
